window.customElements.define(
  'media-gallery',
  class extends window.HTMLElement {
    connectedCallback() {
      // Wait for Splide to be available
      this.waitForSplide().then(() => {
        this.initDOMElements();
        this.initSplide();
        this.initZoom();
        this.initEventListeners();
        this.initStickyGallery();

        // Show gallery and hide placeholder
        this.activateGallery();

        this.scrollToActiveVariantImage();
      });
    }

    waitForSplide() {
      return new Promise((resolve) => {
        if (typeof Splide !== 'undefined') {
          resolve();
        } else {
          const checkSplide = () => {
            if (typeof Splide !== 'undefined') {
              resolve();
            } else {
              setTimeout(checkSplide, 100);
            }
          };
          checkSplide();
        }
      });
    }

    initDOMElements() {
      this.splideSelector = this.querySelector('#splide');
      this.splideThumbnailsSelector = this.querySelector('#splide-thumbnails');
      this.splideSlides = this.querySelectorAll('#splide-slide');

      this.zoomDisplay = document.querySelector('#zoom-display');
      this.loadedZoomImages = new Set();
      this.currentZoomSlide = null;
    }

    initStickyGallery() {
      const galleryItem = this.closest('.product-block-list__item--gallery');
      const wrapper = this.closest('.product-block-list__wrapper');
      
      if (!galleryItem || !wrapper) return;
      
      let ticking = false;
      let galleryOriginalOffsetTop = null;
      let galleryHeight = null;
      let capturedWidth = null;
      let capturedZoomWidth = null;
      let lastWrapperHeight = null;
      let lastGalleryHeight = null;
      
      const updateStickyPosition = () => {
        if (lastGalleryHeight === null || lastWrapperHeight === null) {
          // Measure in static layout so sticky offsets include the badge
          const wasPositioned =
            galleryItem.style.position === 'fixed' || galleryItem.style.position === 'absolute';
          if (wasPositioned) {
            const prev = {
              position: galleryItem.style.position,
              top: galleryItem.style.top,
              left: galleryItem.style.left,
              width: galleryItem.style.width,
            };
            galleryItem.style.position = '';
            galleryItem.style.top = '';
            galleryItem.style.left = '';
            galleryItem.style.width = '';
            lastGalleryHeight = galleryItem.offsetHeight;
            lastWrapperHeight = wrapper.offsetHeight;
            galleryItem.style.position = prev.position;
            galleryItem.style.top = prev.top;
            galleryItem.style.left = prev.left;
            galleryItem.style.width = prev.width;
          } else {
            lastGalleryHeight = galleryItem.offsetHeight;
            lastWrapperHeight = wrapper.offsetHeight;
          }
        }

        const minHeightDifference = 200;
        const hasEnoughHeightDifference = lastWrapperHeight >= lastGalleryHeight + minHeightDifference;
        
        if (window.innerWidth < 1024 || !hasEnoughHeightDifference) {
          galleryItem.style.position = '';
          galleryItem.style.top = '';
          galleryItem.style.left = '';
          galleryItem.style.width = '';
          if (this.zoomDisplay) {
            this.zoomDisplay.style.position = '';
            this.zoomDisplay.style.top = '';
            this.zoomDisplay.style.left = '';
            this.zoomDisplay.style.width = '';
          }
          capturedWidth = null;
          capturedZoomWidth = null;
          ticking = false;
          return;
        }
        
        const wasPositioned = galleryItem.style.position === 'fixed' || galleryItem.style.position === 'absolute';
        if (wasPositioned && capturedWidth === null) {
          galleryItem.style.position = '';
          galleryItem.style.top = '';
          galleryItem.style.left = '';
          galleryItem.style.width = '';
          if (this.zoomDisplay) {
            this.zoomDisplay.style.position = '';
            this.zoomDisplay.style.top = '';
            this.zoomDisplay.style.left = '';
            this.zoomDisplay.style.width = '';
          }
        }
        
        if (galleryOriginalOffsetTop === null) {
          galleryOriginalOffsetTop = galleryItem.offsetTop;
        }
        
        galleryHeight = lastGalleryHeight;
        const wrapperHeight = lastWrapperHeight;
        const stickyOffset = 20;
        
        const hasEnoughSpace = wrapperHeight > galleryHeight + stickyOffset + 50;
        
        if (!hasEnoughSpace) {
          galleryItem.style.position = '';
          galleryItem.style.top = '';
          galleryItem.style.left = '';
          galleryItem.style.width = '';
          if (this.zoomDisplay) {
            this.zoomDisplay.style.position = '';
            this.zoomDisplay.style.top = '';
            this.zoomDisplay.style.left = '';
            this.zoomDisplay.style.width = '';
          }
          capturedWidth = null;
          capturedZoomWidth = null;
          ticking = false;
          return;
        }
        
        const wrapperRect = wrapper.getBoundingClientRect();
        const galleryRect = galleryItem.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        
        const wrapperTop = wrapperRect.top + scrollY;
        const wrapperBottom = wrapperTop + wrapperHeight;
        
        const shouldStick = scrollY + stickyOffset >= wrapperTop;
        const shouldStickToBottom = scrollY + stickyOffset + galleryHeight >= wrapperBottom;
        
        // Capture zoom width when visible (for when it first appears)
        if (this.zoomDisplay && capturedZoomWidth === null && this.zoomDisplay.classList.contains('is-active')) {
          const zoomRect = this.zoomDisplay.getBoundingClientRect();
          if (zoomRect.width > 0) {
            capturedZoomWidth = zoomRect.width;
          }
        }
        
        if (shouldStickToBottom) {
          if (capturedWidth === null) {
            capturedWidth = galleryRect.width;
          }
          
          galleryItem.style.position = 'absolute';
          galleryItem.style.top = (wrapperHeight - galleryHeight) + 'px';
          galleryItem.style.left = '0';
          galleryItem.style.width = capturedWidth + 'px';
          
          // Position zoom display relative to gallery - calculate dynamically
          if (this.zoomDisplay) {
            const gap = 20; // Gap between gallery and zoom
            const zoomLeft = galleryRect.left + capturedWidth + gap;
            const zoomWidth = capturedZoomWidth || capturedWidth; // Fallback to gallery width
            
            this.zoomDisplay.style.position = 'fixed';
            this.zoomDisplay.style.top = stickyOffset + 'px';
            this.zoomDisplay.style.left = zoomLeft + 'px';
            this.zoomDisplay.style.width = zoomWidth + 'px';
          }
        } else if (shouldStick) {
          if (capturedWidth === null) {
            capturedWidth = galleryRect.width;
          }
          
          galleryItem.style.position = 'fixed';
          galleryItem.style.top = stickyOffset + 'px';
          galleryItem.style.left = galleryRect.left + 'px';
          galleryItem.style.width = capturedWidth + 'px';
          
          // Position zoom display relative to gallery - calculate dynamically
          if (this.zoomDisplay) {
            const gap = 20; // Gap between gallery and zoom
            const zoomLeft = galleryRect.left + capturedWidth + gap;
            const zoomWidth = capturedZoomWidth || capturedWidth; // Fallback to gallery width
            
            this.zoomDisplay.style.position = 'fixed';
            this.zoomDisplay.style.top = stickyOffset + 'px';
            this.zoomDisplay.style.left = zoomLeft + 'px';
            this.zoomDisplay.style.width = zoomWidth + 'px';
          }
        } else {
          galleryItem.style.position = '';
          galleryItem.style.top = '';
          galleryItem.style.left = '';
          galleryItem.style.width = '';
          if (this.zoomDisplay) {
            this.zoomDisplay.style.position = '';
            this.zoomDisplay.style.top = '';
            this.zoomDisplay.style.left = '';
            this.zoomDisplay.style.width = '';
          }
          capturedWidth = null;
          capturedZoomWidth = null;
        }
        
        ticking = false;
      };
      
      const requestTick = () => {
        if (!ticking) {
          requestAnimationFrame(updateStickyPosition);
          ticking = true;
        }
      };
      
      const handleResize = () => {
        galleryOriginalOffsetTop = null;
        capturedWidth = null;
        capturedZoomWidth = null;
        lastWrapperHeight = null;
        lastGalleryHeight = null;
        requestTick();
      };
      
      setTimeout(() => {
        updateStickyPosition();
      }, 100);

      // Remeasure when Shopper Approved badge loads / resizes
      const badgeEl = galleryItem.querySelector('.product-gallery__shopper-approved');
      let galleryResizeObserver = null;
      if (badgeEl && typeof ResizeObserver !== 'undefined') {
        galleryResizeObserver = new ResizeObserver(handleResize);
        galleryResizeObserver.observe(badgeEl);
      }

      const badgeImages = galleryItem.querySelectorAll(
        '.product-gallery__shopper-approved img, img[data-sa-badge]'
      );
      const onBadgeLoad = () => {
        lastGalleryHeight = null;
        lastWrapperHeight = null;
        handleResize();
      };
      badgeImages.forEach((img) => {
        if (img.complete) {
          onBadgeLoad();
        } else {
          img.addEventListener('load', onBadgeLoad, { once: true });
        }
      });
      
      window.addEventListener('scroll', requestTick, { passive: true });
      window.addEventListener('resize', handleResize);
      
      this._stickyCleanup = () => {
        window.removeEventListener('scroll', requestTick);
        window.removeEventListener('resize', handleResize);
        if (galleryResizeObserver) galleryResizeObserver.disconnect();
        badgeImages.forEach((img) => {
          img.removeEventListener('load', onBadgeLoad);
        });
        galleryItem.style.position = '';
        galleryItem.style.top = '';
        galleryItem.style.left = '';
        galleryItem.style.width = '';
        if (this.zoomDisplay) {
          this.zoomDisplay.style.position = '';
          this.zoomDisplay.style.top = '';
          this.zoomDisplay.style.left = '';
          this.zoomDisplay.style.width = '';
        }
      };
    }
    
    // Activate gallery and remove placeholder
    activateGallery() {
      const placeholder = document.getElementById('gallery-placeholder');
      const gallery = this;

      if (placeholder) {
        // Show the gallery
        gallery.classList.remove('hidden');
        
        // Remove the placeholder
        placeholder.remove();
      }
    }

    initSplide() {
      // Initialize main gallery first
      this.splide = new Splide(this.splideSelector, {
        type: 'slide',
        perPage: 1,
        pagination: false,
        arrows: true,
        speed: 800, // Smooth transition speed
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Smooth easing
        breakpoints: {
          1024: {
            arrows: false,
          },
        },
      });

      // Initialize thumbnails with Splide's built-in options
      this.splideThumbnails = new Splide(this.splideThumbnailsSelector, {
        type: 'slide',
        perPage: 4,
        gap: '8px',
        perMove: 3, // Move by perPage amount
        pagination: false,
        arrows: true,
        focus: false, // Prevent auto-centering
        trimSpace: true, // This prevents empty space at the end
        drag: true, // Enable dragging for mobile
        wheel: false, // Disable mouse wheel
        flickPower: 600, // Control flick sensitivity on mobile
        dragMinThreshold: {
          mouse: 3,
          touch: 10,
        },
        breakpoints: {
          1024: {
            arrows: false,
            drag: true, // Ensure drag is enabled on mobile
            perMove: 3, // Move by 3 on tablet and below
          },
          768: {
            perPage: 3, // Show 3 thumbnails on tablet
            gap: '6px',
            perMove: 3,
          },
          480: {
            perPage: 2, // Show 2 thumbnails on mobile
            gap: '6px',
            perMove: 2,
          },
        },
      });

      // Event listeners for main slider
      this.splide.on('moved', (newIndex) => {
        this.hideZoomDisplay();
        this.hideAllLenses();
        this.clearZoomDisplay(); // Clear zoom display when slide changes
        this.updateMainNavigation();
        this.updateThumbnailActiveState(newIndex);
        
        // Re-show lens if mouse is still hovering over the new active slide
        this.handleSlideChange(newIndex);
      });

      this.splide.on('mounted', () => {
        this.updateMainNavigation();
        this.updateThumbnailActiveState(0);
        this.addThumbnailClickListeners();
      });

      // Event listeners for thumbnails
      this.splideThumbnails.on('mounted', () => {
        this.updateThumbnailsNavigation();
      });

      this.splideThumbnails.on('moved', () => {
        this.updateThumbnailsNavigation();
      });

      // Mount the sliders
      this.splide.mount();
      this.splideThumbnails.mount();
    }

    // New method to handle slide changes while hovering
    handleSlideChange(newIndex) {
      if (this.isMouseOverTrigger && this.currentHoveredSlide === newIndex) {
        // User is still hovering over the new active slide
        const activeSlide = this.splideSlides[newIndex];
        if (activeSlide) {
          const trigger = activeSlide.querySelector('.zoom-trigger');
          const lens = trigger?.querySelector('[data-lens]');
          
          if (trigger && lens) {
            // Re-show the lens for the new active slide
            lens.classList.remove('hidden');
            
            // Load zoom image and show zoom display
            this.loadZoomImageForSlide(newIndex, () => {
              this.showZoomDisplayFromSlider(newIndex);
            });
          }
        }
      }
    }

    addThumbnailClickListeners() {
      const thumbnailSlides = this.splideThumbnailsSelector.querySelectorAll('.splide__slide');
      
      thumbnailSlides.forEach((slide, index) => {
        slide.addEventListener('click', (e) => {
          // Only prevent default for desktop to avoid interfering with mobile touch
          if (window.innerWidth >= 1024) {
            e.preventDefault();
            e.stopPropagation();
          }
          
          this.updateThumbnailActiveState(index);
          this.splide.go(index);
        });
      });
    }

    updateThumbnailActiveState(activeIndex) {
      const thumbnailSlides = this.splideThumbnailsSelector.querySelectorAll('.splide__slide');
      
      // Remove our custom active class from all thumbnails
      thumbnailSlides.forEach((slide) => {
        slide.classList.remove('main-image-active');
      });
      
      // Add our custom active class to the thumbnail that matches the main gallery's current slide
      if (thumbnailSlides[activeIndex]) {
        thumbnailSlides[activeIndex].classList.add('main-image-active');
      }
    }

    initZoom() {
      if (window.innerWidth < 1024 || !this.zoomDisplay) return;

      const zoomTriggers = this.querySelectorAll('.zoom-trigger');

      zoomTriggers.forEach((trigger, index) => {
        const img = trigger.querySelector('.zoom-image');
        const lens = trigger.querySelector('[data-lens]');
        if (!img || !lens) return;

        let cx, cy;
        let hasMouseMoved = false; // Track if mouse has moved since page load
        let isCurrentlyHovering = false; // Track current hover state

        trigger.addEventListener('mouseenter', () => {
          const slideIndex = parseInt(trigger.dataset.slideIndex);
          
          this.isMouseOverTrigger = true;
          this.currentHoveredSlide = slideIndex;
          isCurrentlyHovering = true;
          
          if (slideIndex !== this.splide.index) return;

          if (!hasMouseMoved) return;

          lens.classList.remove('hidden');
          
          this.loadZoomImageForSlide(slideIndex, () => {
            // CHECK if still hovering before showing zoom
            if (isCurrentlyHovering && slideIndex === this.splide.index) {
              this.showZoomDisplayFromSlider(slideIndex);
              cx = this.zoomDisplay.offsetWidth / lens.offsetWidth;
              cy = this.zoomDisplay.offsetHeight / lens.offsetHeight;
            }
          });
        });


        trigger.addEventListener('mouseleave', () => {
          const slideIndex = parseInt(trigger.dataset.slideIndex);
          
          // Clear hover state
          this.isMouseOverTrigger = false;
          this.currentHoveredSlide = null;
          isCurrentlyHovering = false;
          
          lens.classList.add('hidden');
          this.hideZoomDisplay();
        });

        trigger.addEventListener('mousemove', (e) => {
          const slideIndex = parseInt(trigger.dataset.slideIndex);
          
          if (!hasMouseMoved) {
            hasMouseMoved = true;
            if (slideIndex === this.splide.index && isCurrentlyHovering) {
              lens.classList.remove('hidden');
              this.loadZoomImageForSlide(slideIndex, () => {
                // CHECK if still hovering before showing zoom
                if (isCurrentlyHovering && slideIndex === this.splide.index) {
                  this.showZoomDisplayFromSlider(slideIndex);
                  cx = this.zoomDisplay.offsetWidth / lens.offsetWidth;
                  cy = this.zoomDisplay.offsetHeight / lens.offsetHeight;
                }
              });
            }
          }
          
          if (slideIndex !== this.splide.index) return;

          this.updateLensPosition(e, trigger, lens);
          this.updateZoomPosition(e, trigger, lens, cx, cy);
        });
      });
    }

    // New method to load zoom image only when needed
    loadZoomImageForSlide(slideIndex, callback) {
      const activeSlide = this.splideSlides[slideIndex];
      if (!activeSlide) return;

      const img = activeSlide.querySelector('.zoom-image');
      if (!img) return;

      // Priority: data-zoom-url > enhanced img.src > original img.src
      let zoomImageUrl = img.dataset.zoomUrl || this.getEnhancedImageUrl(img.src);

      // Check if already loaded
      if (this.loadedZoomImages.has(slideIndex) && this.currentZoomSlide === slideIndex) {
        if (callback) callback();
        return;
      }

      this.preloadAndSetZoomImage(zoomImageUrl, img.src, slideIndex, callback);
    }

    // Helper method to get enhanced image URL
    getEnhancedImageUrl(originalSrc) {
      if (originalSrc.includes('shopify')) {
        return originalSrc.replace(/(_\d+x\d+)/g, '_1600x1200');
      }
      return originalSrc;
    }

    // Helper method to preload and set zoom image
    preloadAndSetZoomImage(zoomUrl, fallbackUrl, slideIndex, callback) {
      const zoomImg = new Image();
      
      zoomImg.onload = () => {
        this.setZoomBackground(zoomUrl);
        this.markZoomImageLoaded(slideIndex);
        if (callback) callback();
      };

      zoomImg.onerror = () => {
        this.setZoomBackground(fallbackUrl);
        this.markZoomImageLoaded(slideIndex);
        if (callback) callback();
      };

      zoomImg.src = zoomUrl;
    }

    // Helper method to set zoom background
    setZoomBackground(imageUrl) {
      this.zoomDisplay.style.backgroundImage = `url('${imageUrl}')`;
      this.zoomDisplay.style.backgroundSize = '250% 250%';
      this.zoomDisplay.style.backgroundRepeat = 'no-repeat';
    }

    // Helper method to mark zoom image as loaded
    markZoomImageLoaded(slideIndex) {
      this.loadedZoomImages.add(slideIndex);
      this.currentZoomSlide = slideIndex;
    }

    initEventListeners() {
      window.addEventListener('variant:changed', (e) => {
        const variantId = e.detail.variant.id;
        const index = this.getActiveVariantImageIndex(variantId);

        if (index !== null) {
          this.splide.go(parseInt(index));
          // updateThumbnailActiveState will be called by the main gallery's 'moved' event
        }
      });
    }

    // Add this new method after initEventListeners()

    // Add cleanup when component is removed
    disconnectedCallback() {
      if (this._stickyCleanup) {
        this._stickyCleanup();
      }
    }
    
    updateLensPosition(e, trigger, lens) {
      const rect = trigger.getBoundingClientRect();
      let x = e.clientX - rect.left - lens.offsetWidth / 2;
      let y = e.clientY - rect.top - lens.offsetHeight / 2;

      // Keep lens within image boundaries
      if (x < 0) x = 0;
      if (y < 0) y = 0;
      if (x > trigger.offsetWidth - lens.offsetWidth) {
        x = trigger.offsetWidth - lens.offsetWidth;
      }
      if (y > trigger.offsetHeight - lens.offsetHeight) {
        y = trigger.offsetHeight - lens.offsetHeight;
      }

      lens.style.left = x + 'px';
      lens.style.top = y + 'px';
    }

    updateZoomPosition(e, trigger, lens, cx, cy) {
      if (!this.zoomDisplay || !this.zoomDisplay.classList.contains('is-active')) return;

      const rect = trigger.getBoundingClientRect();
      const x = e.clientX - rect.left - lens.offsetWidth / 2;
      const y = e.clientY - rect.top - lens.offsetHeight / 2;

      const constrainedX = Math.max(0, Math.min(x, trigger.offsetWidth - lens.offsetWidth));
      const constrainedY = Math.max(0, Math.min(y, trigger.offsetHeight - lens.offsetHeight));

      const percentX = (constrainedX / (trigger.offsetWidth - lens.offsetWidth)) * 100;
      const percentY = (constrainedY / (trigger.offsetHeight - lens.offsetHeight)) * 100;

      this.zoomDisplay.style.backgroundPosition = `${percentX}% ${percentY}%`;
    }

    showZoomDisplayFromSlider(slideIndex) {
      if (!this.zoomDisplay) return;

      this.zoomDisplay.classList.add('is-active');
    }

    hideZoomDisplay() {
      if (!this.zoomDisplay) return;

      this.zoomDisplay.classList.remove('is-active');
    }

    // New method to clear zoom display when slide changes
    clearZoomDisplay() {
      if (!this.zoomDisplay) return;
      
      // Clear the background image
      this.zoomDisplay.style.backgroundImage = '';
      this.currentZoomSlide = null;
    }

    hideAllLenses() {
      const allLenses = this.querySelectorAll('[data-lens]');
      allLenses.forEach((lens) => lens.classList.add('hidden'));
    }

    // Keep this method but it's now only called on demand
    preloadActiveSlideImage() {
      const activeIndex = this.splide ? this.splide.index : 0;
      this.loadZoomImageForSlide(activeIndex);
    }

    // Utils
    updateThumbnailsNavigation() {
      const prevButton = this.splideThumbnailsSelector.querySelector('.splide__arrow--prev');
      const nextButton = this.splideThumbnailsSelector.querySelector('.splide__arrow--next');

      if (this.splideThumbnails.index === 0) {
        this.hideElementDesktop(prevButton);
      } else {
        this.showElementDesktop(prevButton);
      }

      if (this.splideThumbnails.index >= this.splideThumbnails.length - this.splideThumbnails.options.perPage) {
        this.hideElementDesktop(nextButton);
      } else {
        this.showElementDesktop(nextButton);
      }
    }

    updateMainNavigation() {
      const prevButton = this.splideSelector.querySelector('.splide__arrow--prev');
      const nextButton = this.splideSelector.querySelector('.splide__arrow--next');

      if (this.splide.index === 0) {
        this.hideElementMobile(prevButton);
      } else {
        this.showElementMobile(nextButton);
      }

      if (this.splide.index >= this.splide.length - 1) {
        this.hideElementMobile(nextButton);
      } else {
        this.showElementMobile(nextButton);
      }
    }

    hideElementMobile(element) {
      if (element) {
        element.classList.add('!hidden');
        element.classList.remove('!flex');
      }
    }

    hideElementDesktop(element) {
      if (element) {
        element.classList.add('lg:!hidden');
        element.classList.remove('lg:!flex');
      }
    }

    showElementMobile(element) {
      if (element) {
        element.classList.remove('!hidden');
        element.classList.add('!flex');
      }
    }

    showElementDesktop(element) {
      if (element) {
        element.classList.remove('lg:!hidden');
        element.classList.add('lg:!flex');
      }
    }

    getActiveVariantImageIndex(variantId) {
      const targetSlide = Array.from(this.splideSlides).find(
        (slide) => slide.dataset.variantId === variantId.toString(),
      );

      const index = targetSlide ? targetSlide.dataset.index : null;

      return index;
    }

    scrollToActiveVariantImage() {
      const activeVariantId = this.splideSelector.dataset.activeVariantId;

      const index = this.getActiveVariantImageIndex(activeVariantId);
      if (index !== null) {
        const slideIndex = parseInt(index);
        this.splide.go(slideIndex);
        this.updateThumbnailActiveState(slideIndex);
      }
    }
  },
);